import { motion } from 'framer-motion';
import { useState } from 'react';
import { ArrowRight, Play, Check } from 'lucide-react';
import { AnimatedText } from '../components/AnimatedText';

export function Hero() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [dragKey, setDragKey] = useState(0); // Force re-render to reset position

  const handleDoubleClick = () => {
    setDragKey(prev => prev + 1); // Reset position on double-click
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section
      id="home"
      className="relative min-h-[90vh] flex items-center overflow-hidden"
    >
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center bottom' }}
        >
          <source src="/hero-video.mov" type="video/quicktime" />
          <source src="/hero-video.mov" type="video/mp4" />
        </video>
      </div>
      
      {/* Gradient Overlay (Reduced Opacity) */}
      <div className="absolute inset-0 z-10 bg-gradient-to-br from-dark-900/85 via-dark-950/80 to-dark-800/85" />
      
      {/* Animated Background Shapes - ABOVE gradient, HIGHER opacity */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear',
        }}
        className="absolute z-20 top-20 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-primary-600/30 to-primary-800/20 rounded-full blur-3xl"
      />
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          rotate: [0, -60, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: 'linear',
        }}
        className="absolute z-20 -bottom-40 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-primary-700/30 to-primary-900/20 rounded-full blur-3xl"
      />

      {/* Grid Pattern (Reduced Opacity) */}
      <div 
        className="absolute z-10 inset-0 opacity-[0.01]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-20 max-w-7xl mx-auto section-padding py-16 lg:py-24">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl"
        >
          {/* Badge */}
          <motion.div variants={itemVariants}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full shadow-sm"
            >
              <span className="text-primary-400">✨</span>
              <span className="text-sm font-medium text-white/90">
                300+ Bisnis Telah Kami Bantu
              </span>
            </motion.div>
          </motion.div>

          {/* Main Heading */}
          <motion.div variants={itemVariants}>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6 tracking-[0.1em]">
              <span className="block">
                <AnimatedText text="YOUR BUSINESS" />
              </span>
              <span className="block mt-2">
                <span className="text-gradient">
                  <AnimatedText text="& MARKET SOLUTION" delay={0.3} />
                </span>
              </span>
            </h1>
          </motion.div>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="text-lg md:text-xl text-white/70 max-w-xl mb-10 leading-relaxed"
          >
            Membantu 300+ bisnis sejak tahun 2022 di bidang IT, Manufaktur, F&B, Pariwisata, Konstruksi & Retail. Partner terpercaya untuk kesuksesan bisnis Anda.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4">
            <motion.a
              href="#services"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' });
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group btn-primary inline-flex items-center justify-center gap-2"
            >
              Jelajahi Layanan
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </motion.a>

            <motion.a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group btn-secondary inline-flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Pelajari Lebih Lanjut
            </motion.a>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            variants={itemVariants}
            className="mt-12 pt-6 border-t border-dark-200/50 pb-0"
          >
            <p className="text-sm text-white/50 mb-6">Telah dipercaya oleh klien-klien kami:</p>
            <div className="grid grid-cols-5 gap-4 opacity-80">
              {[
                { name: 'BERMAN', logo: '/client-berman.png' },
                { name: 'Panca Prima Wijaya', logo: '/client-panca-prima-wijaya.png' },
                { name: 'Deo Airport', logo: '/client-deo-airport.png' },
                { name: 'Askeba', logo: '/client-askeba.png' },
                { name: 'Bumi Anugerah Jaya', logo: '/client-bumi-anugerah-jaya.png' },
                { name: 'Lumina', logo: '/client-lumina.png' },
                { name: 'Cireng Keraton', logo: '/client-cireng-keraton.png' },
                { name: 'Pro Familie', logo: '/client-pro-familie.png' },
                { name: 'TECI', logo: '/client-teci.png' },
                { name: 'PusatInvestasi.id', logo: '/client-pusat-investasi.png' },
              ].map((client, i) => (
                <motion.div
                  key={client.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.1 }}
                  className="h-20 flex items-center justify-center"
                >
                  <img 
                    src={client.logo} 
                    alt={client.name}
                    className="h-full w-auto object-contain"
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Right Side Decorative Element */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, x: 100 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:block absolute -right-40 xl:-right-36 top-1/4"
          style={{ transform: 'translateY(-50%)' }}
        >
          <motion.div
            key={dragKey}
            drag
            dragConstraints={{ left: -1200, right: 300, top: -400, bottom: 400 }}
            dragElastic={0.05}
            dragMomentum={true}
            dragTransition={{ bounceStiffness: 400, bounceDamping: 25 }}
            onDoubleClick={handleDoubleClick}
            whileDrag={{ scale: 1.05, cursor: 'grabbing' }}
            className="relative cursor-grab"
            style={{ transform: 'rotate(5deg)', transformOrigin: 'center center' }}
          >
            {/* Main floating card */}
            <motion.div
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              className="relative w-60 min-h-[288px] rounded-3xl p-6 shadow-2xl bg-white/5 backdrop-blur-md border border-white/10"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden bg-white">
                  <img src="/emp-logo.png" alt="Emporium Solution Logo" className="w-10 h-10 object-contain" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm whitespace-nowrap">Emporium Solution</p>
                  <p className="text-xs text-white/70">Konsultasi Bisnis</p>
                </div>
              </div>

              {/* Testimonials - macOS Dock Style + Click Expand */}
              <div className="mb-3">
                <p className="text-xs text-white/80 font-bold">Bisnis yang Telah Kami Bantu:</p>
              </div>
              <div className="flex flex-col gap-2">
                {[
                  { 
                    company: 'PT Indo Human Resource', 
                    logo: '/indohr-logo.jpg',
                    name: 'Ari',
                    quote: 'Business Plan-nya lengkap, jelas, dan menjawab kebutuhan pasar kami. Tim Emporium sangat kooperatif dan komunikatif dalam memberikan insight perencanaan go-to-market plan.'
                  },
                  { 
                    company: 'PT Tani Konjac Indo', 
                    logo: '/tkonia-logo.jpg',
                    name: 'Herry Susilo',
                    quote: 'Tim Emporium kompak menata sistem manajemen kami dan turut terjun langsung dalam operasional serta melakukan evaluasi berkala, sangat kooperatif kami terbantu dengan service dari tim Emporium'
                  },
                  { 
                    company: 'PT Jakarta Utilitas Propertindo', 
                    logo: '/jup-logo.jpg',
                    name: 'Nurul',
                    quote: 'Pak Daniel dan tim Emporium, cepet tanggap dan sangat membantu dalam menerapkan operasional, SOP dan penetapan KPI operasional kami, proyeksi yang dibuat juga detail dan membantu dalam decision making, tim responsive dan komunikatif jadi kami nyaman bekerja dengan tim pak Daniel'
                  },
                ].map((item, i) => {
                  const isExpanded = expandedIndex === i;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1 + i * 0.2 }}
                      whileHover={{ scale: isExpanded ? 1 : 1.15, zIndex: 10 }}
                      onClick={() => setExpandedIndex(isExpanded ? null : i)}
                      className="relative p-2 bg-white/40 rounded-xl cursor-pointer overflow-hidden"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-white">
                          <img src={item.logo} alt={item.company} className="w-full h-full object-cover" />
                        </div>
                        <span className={`text-xs font-medium text-dark-900 ${isExpanded ? '' : 'truncate'}`}>
                          {item.company}
                        </span>
                      </div>
                      
                      {/* Expandable content */}
                      <motion.div
                        initial={false}
                        animate={{ 
                          height: isExpanded ? 'auto' : 0,
                          opacity: isExpanded ? 1 : 0
                        }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3 pb-1 px-1">
                          <p className="text-xs text-dark-700 leading-relaxed mb-2">"{item.quote}"</p>
                          <p className="text-xs font-semibold text-primary-600">- {item.name}</p>
                        </div>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </div>

              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -top-5 -right-5 w-12 h-12 bg-green-700 rounded-2xl shadow-lg flex items-center justify-center"
              >
                <Check className="w-8 h-8 text-white" strokeWidth={6} />
              </motion.div>
            </motion.div>

            {/* Secondary floating elements - moves with drag */}
            <motion.div
              animate={{ y: [0, 15, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
              className="absolute -bottom-6 -left-6 w-24 h-16 glass rounded-2xl shadow-lg flex items-center justify-center"
            >
              <div className="text-center">
                <p className="text-2xl font-bold text-primary-400">300+</p>
                <p className="text-xs text-white/70">Klien</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
